import React, { useState, useEffect } from 'react';
import axios from 'axios';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import {
    Box, Typography, Paper, List, ListItem, ListItemText, Checkbox,
     CircularProgress, Alert, Button, IconButton, Badge, Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import { styled } from '@mui/system';
import NotificationsIcon from '@mui/icons-material/Notifications';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

dayjs.extend(relativeTime);

const PRIMARY_DARK = '#11224d';
const SECONDARY_DARK = '#0e1d3a';
const ACCENT_GOLD = '#ffb300';

const NotificationContainer = styled(Box)({
    background: '#f7f8fc',
    minHeight: '100vh',
    padding: 0,
    margin: 0,
});

const NotificationPaper = styled(Paper)({
    width: '100%',
    height: '100%',
    borderRadius: 0,
    boxShadow: 'none',
    background: '#fff',
});

const HeaderSection = styled(Box)({
    background: `linear-gradient(135deg, ${PRIMARY_DARK}, ${SECONDARY_DARK})`,
    padding: '10px 18px',  
    color: '#fff',
    borderBottom: `3px solid ${ACCENT_GOLD}`, 
});


const NotificationList = styled(List)({
    padding: '0',
});

const NotificationItem = styled(ListItem)(({ isRead }) => ({
    backgroundColor: isRead ? '#fafafa' : '#fff',
    borderLeft: isRead ? '4px solid transparent' : `4px solid ${ACCENT_GOLD}`,
    margin: 0,            
    padding: '12px 18px',
    borderRadius: 0,
    width: '100%',
    cursor: 'pointer',
    transition: '0.2s ease',
    borderBottom: '1px solid #eee',
    '&:hover': {
        backgroundColor: isRead ? '#f3f3f3' : '#fff6db',
    }
}));

const StyledButton = styled(Button)({
    backgroundColor: ACCENT_GOLD,
    color: PRIMARY_DARK,
    fontWeight: 600,
    textTransform: 'none',
    padding: '8px 20px',
    borderRadius: 6,
    '&:hover': {
        backgroundColor: '#ffc107',
    },
});

const Notifications = ({ onNotificationsRead, onNotificationClick }) => {
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selected, setSelected] = useState([]);
    const [confirmDialog, setConfirmDialog] = useState({ open: false, ids: [], message: '' });
    const [selectionMode, setSelectionMode] = useState(false);


    const API_BASE_URL = process.env.REACT_APP_API_BASE || 'http://localhost:5000';

    const fetchNotifications = async () => {
        try {
            const token = localStorage.getItem('jwtToken');
            const { data } = await axios.get(`${API_BASE_URL}/api/notifications`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setNotifications(data);
        } catch (err) {
            setError('Failed to fetch notifications.');
        } finally {
            setLoading(false);
        }
    };

    const markAllAsRead = async () => {
        try {
            const token = localStorage.getItem('jwtToken');
            await axios.patch(`${API_BASE_URL}/api/notifications/read-all`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });

            setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
            onNotificationsRead();
        } catch (err) {
            console.error('Failed to mark read');
        }
    };

    const markNotificationAsRead = async (notificationId) => {
        try {
            const token = localStorage.getItem('jwtToken');
            await axios.patch(`${API_BASE_URL}/api/notifications/${notificationId}/read`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
           
            setNotifications(prev => prev.map(n =>
                n.id === notificationId ? { ...n, is_read: true } : n
            ));
            onNotificationsRead(); 
        } catch (err) {
            console.error('Failed to mark notification as read:', err);
            setError('Failed to mark notification as read.');
        }
    };

    const handleDeleteRequest = (notificationIds) => {
        const message = notificationIds.length > 1
            ? `Are you sure you want to delete these ${notificationIds.length} notifications?`
            : 'Are you sure you want to delete this notification?';

        setConfirmDialog({
            open: true,
            ids: notificationIds,
            message: message,
        });
    };

    const handleConfirmDelete = async () => {
        const idsToDelete = confirmDialog.ids;
        if (idsToDelete.length === 0) return;

        try {
            const token = localStorage.getItem('jwtToken');
            const headers = { Authorization: `Bearer ${token}` };

            if (idsToDelete.length === 1) {
                await axios.delete(`${API_BASE_URL}/api/notifications/${idsToDelete[0]}`, { headers });
            } else {
                await axios.post(`${API_BASE_URL}/api/notifications/delete-multiple`, { ids: idsToDelete }, { headers });
            }

            setNotifications(prev => prev.filter(n => !idsToDelete.includes(n.id)));
            setSelected(prev => prev.filter(id => !idsToDelete.includes(id)));
            onNotificationsRead(); 
        } catch (err) {
            console.error('Failed to delete notification(s):', err);
            setError('Failed to delete notification(s).');
        } finally {
            setConfirmDialog({ open: false, ids: [], message: '' });
            handleExitSelectionMode();
        }
    };

    const handleNotificationItemClick = (notification) => {
        if (!notification.is_read) {
            markNotificationAsRead(notification.id);
        }
        if (onNotificationClick) {
            onNotificationClick(notification.link);
        }
    };

    const handleEnterSelectionMode = (notificationId) => {
        setSelectionMode(true);
    
        setSelected([notificationId]);
    };

    const handleExitSelectionMode = () => {
        setSelectionMode(false);
        setSelected([]);
    };

    const handleSelect = (notificationId) => {
        const selectedIndex = selected.indexOf(notificationId);
        let newSelected = [];

        if (selectedIndex === -1) {
            newSelected = newSelected.concat(selected, notificationId);
        } else {
            newSelected = selected.filter(id => id !== notificationId);
        }
        setSelected(newSelected);
    };

    const handleSelectAll = (event) => {
        if (event.target.checked) {
            const newSelecteds = notifications.map((n) => n.id);
            setSelected(newSelecteds);
            return;
        }
        setSelected([]);
    };

    useEffect(() => {
        fetchNotifications();
    }, []);

    const unreadCount = notifications.filter(n => !n.is_read).length;
    const isSelected = (id) => selected.indexOf(id) !== -1;

    if (loading) {
        return (
            <NotificationContainer>
                <Box display="flex" justifyContent="center" minHeight="400px">
                    <CircularProgress sx={{ color: ACCENT_GOLD }} size={60} />
                </Box>
            </NotificationContainer>
        );
    }

    if (error) {
        return (
            <NotificationContainer>
                <Alert severity="error" sx={{ borderRadius: 0 }}>{error}</Alert>
            </NotificationContainer>
        );
    }

    return (
        <NotificationContainer>
            <NotificationPaper>

                <HeaderSection>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Box display="flex" alignItems="center" gap={2}>
                            <Badge
                                badgeContent={unreadCount}
                                invisible={unreadCount === 0}
                                sx={{
                                    '& .MuiBadge-badge': {
                                        backgroundColor: ACCENT_GOLD,
                                        color: PRIMARY_DARK,
                                        fontWeight: 700,
                                    }
                                }}
                            >
                                <NotificationsIcon sx={{ fontSize: 36 }} />
                            </Badge>

                            <Box>
                                <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.1 }}>
                                    Notifications
                                </Typography>
                                <Typography variant="caption" sx={{ opacity: 0.8 }}>
                                    {unreadCount === 0
                                        ? "You're all caught up!"
                                        : `You have ${unreadCount} unread notification(s)`}
                                </Typography>
                            </Box>

                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            {selectionMode && (
                                <>
                                    <Button
                                        variant="contained"
                                        color="error"
                                        size="small"
                                        startIcon={<DeleteOutlineIcon />}
                                        onClick={() => handleDeleteRequest(selected)}
                                        disabled={selected.length === 0}
                                        sx={{ textTransform: 'none' }}
                                    >
                                        Delete ({selected.length})
                                    </Button>
                                    <Button 
                                        size="small" 
                                        onClick={handleExitSelectionMode} 
                                        sx={{ textTransform: 'none', color: 'white', borderColor: 'white', '&:hover': { borderColor: '#ffb300', color: '#ffb300' } }}
                                        variant="outlined"
                                    >
                                        Cancel
                                    </Button>
                                </>
                            )}

                           
                            {unreadCount > 0 && !selectionMode && notifications.length > 0 && (
                                <StyledButton
                                    startIcon={<CheckCircleOutlineIcon />}
                                    onClick={markAllAsRead}
                                >
                                    Mark All as Read
                                </StyledButton>
                            )}
                        </Box>
                    </Box>
                </HeaderSection>

             
                <NotificationList>
                    {selectionMode && notifications.length > 0 && (
                        <ListItem sx={{ background: '#f1f1f1', py: 0.5 }}>
                            <Checkbox
                                indeterminate={selected.length > 0 && selected.length < notifications.length}
                                checked={notifications.length > 0 && selected.length === notifications.length}
                                onChange={handleSelectAll}
                                inputProps={{ 'aria-label': 'select all notifications' }}
                                size="small"
                            />
                            <ListItemText primary="Select All" primaryTypographyProps={{ fontWeight: 'bold', fontSize: '0.8rem' }} />
                        </ListItem>
                    )}
                    {notifications.map(notification => {
                        const isItemSelected = isSelected(notification.id);
                        const handleItemClick = () => {
                            if (selectionMode) {
                                handleSelect(notification.id);
                            } else {
                                handleNotificationItemClick(notification);
                            }
                        };
                        return (
                            <NotificationItem
                                key={notification.id}
                                isRead={notification.is_read}
                                onClick={handleItemClick}
                            >
                                {selectionMode && (
                                    <Checkbox
                                        checked={isItemSelected}
                                        size="small"
                                        onClick={(e) => e.stopPropagation()} 
                                    />
                                )}
                                <Box flex={1}>
                                    <Typography
                                        variant="body1"
                                        fontWeight={notification.is_read ? 400 : 600}
                                    >
                                        {notification.message}
                                    </Typography>

                                    <Typography variant="caption" sx={{ color: '#777' }}>
                                        {dayjs(notification.created_at).fromNow()}
                                    </Typography>
                                </Box>

                                {!selectionMode && (
                                    <IconButton size="small" onClick={(e) => {
                                        e.stopPropagation(); 
                                        handleEnterSelectionMode(notification.id);
                                    }}>
                                        <DeleteOutlineIcon fontSize="small" />
                                    </IconButton>
                                )}
                            </NotificationItem>
                        );
                    })}
                </NotificationList>

            </NotificationPaper>

            <Dialog
                open={confirmDialog.open}
                onClose={() => setConfirmDialog({ ...confirmDialog, open: false })}
            >
                <DialogTitle>Confirm Deletion</DialogTitle>
                <DialogContent>
                    <Typography>{confirmDialog.message}</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmDialog({ ...confirmDialog, open: false })}>Cancel</Button>
                    <Button onClick={handleConfirmDelete} color="error" autoFocus>
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </NotificationContainer>
    );
};

export default Notifications;
             